import { Fragment, FunctionComponent, h } from "preact"
import { useState } from "preact/hooks"
import { FaRegFileExcel } from "react-icons/fa"
import Container from "../../parts/container"
import TopBar from "../../components/topbar"
import TopFilter from "../../components/TopFilter"
import Breadcrumb from "../../components/topbar/breadcrumb"
import ServerPagination from "../../components/pagination/server-pagination"
import { sortActiveParam, isAvgSensor } from "../../utils/sortParam"
import { toFixed } from "../../utils/toFixed"
import { useFilterContext } from "../../context/useFilter"
import { defaultParams } from "../../utils/defaults"
import Spinner from "../../components/spinner"
import { useRecordPaginate } from "../../hooks/useSensor"
import useSites from "../../hooks/useSite"
import { formatDateWithPeriod } from "../../utils/dateformat"
import useObjectState from "../../hooks/useObjectState"
import { Button } from "../../parts/buttons"
import { sensorDetailToExcel } from "./toexcel"
import { can } from "../../utils/access"
import { Permission } from "../../enum/permissions.enum"
import style from "./style.scss"
import { useSortHeader } from "../../hooks/useSortHeader"
import { useTranslation } from "react-i18next"

type RecordSensorDataProps = h.JSX.HTMLAttributes<HTMLDivElement>

const RecordSensorDataPage: FunctionComponent<RecordSensorDataProps> = () => {
  const { t } = useTranslation()
  const { contextFilter, setContextFilter } = useFilterContext()
  const { data: cems } = useSites()
  const [filtered, setFiltered] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  let defaultValue = {
    site: undefined,
    period: undefined,
    startDate: undefined,
    endDate: undefined,
    orders: [],
    ...contextFilter,
    sensor: Array.isArray(contextFilter?.sensor) ? contextFilter?.sensor?.[0] : contextFilter?.sensor,
  }
  const [payload, setPayload] = useObjectState(defaultValue)

  const { data, exportExcel } = useRecordPaginate({
    uuid: payload.site,
    type: payload.period,
    start: payload.startDate,
    end: payload.endDate,
    page: currentPage,
    parameter_id: payload.sensor_id,
    orders: payload.orders,
    limit: 20,
  })

  const columns = sortActiveParam(data?.parameter) || defaultParams

  const getData = (data) => {
    return data?.record?.map((item) => {
      const filterRecord = item?.data?.filter(({ name }) => columns?.map(({ name: colName }) => colName)?.includes(name))
      const record = filterRecord?.find(({ name }) => name === payload.sensor)
      const parameter = data?.parameter?.find(({ name }) => name === payload.sensor)
      const result = {
        timestamp: item.timestamp,
        value: isAvgSensor(payload.period, payload.sensor) ? toFixed(record?.avg) : toFixed(record?.value),
        min: toFixed(parameter?.lower_threshold),
        max: toFixed(parameter?.upper_threshold),
        battery: payload.period === "raw" ? toFixed(record?.battery_percentage, 2) : toFixed(record?.avg_b_percentage, 2),
        delay: payload.period === "raw" ? toFixed(record?.delay, 2) : toFixed(record?.avg_delay, 2),
      }
      return result
    })
  }

  const tableData = payload.sensor ? getData(data) : []

  const handleFilterData = (data) => {
    resetSorting()
    setPayload({ ...data, orders: [] })
    setFiltered(true)
    setContextFilter(data)
    setCurrentPage(1)
  }

  const getSiteName = (siteId) => cems?.find(({ uuid }) => uuid === siteId)?.name

  const getExcelData = async () => {
    const exportExcelData = await exportExcel(data?.totalRecords)
    const excelData = payload.sensor ? getData(exportExcelData) : []
    if (excelData?.length) {
      sensorDetailToExcel(excelData, payload.period, payload.sensor, getSiteName(payload.site))
    }
  }

  // order data
  const { sort, onOrder, sortIcon, resetSorting } = useSortHeader({
    initialFieldToSort: {
      timestamp: null,
      value: null,
      ["lower-threshold"]: null,
      ["upper-threshold"]: null,
      battery: null,
      delay: null,
    },
  })

  const onSortField = (field: string) => {
    const sortParamsData = onOrder(field)
    setPayload({ ...payload, orders: sortParamsData })
  }
  // end order data

  return (
    <div className="w-100">
      <TopBar>
        <Breadcrumb name={t("breadcrumb.record")} link="" />
        <Breadcrumb name={t("breadcrumb.sensor_detail")} link="/record/sensor-detail" />
      </TopBar>
      <Container>
        {data ? (
          <Fragment>
            <div className="row align-items-stretch">
              <TopFilter cems={cems} inputSensor="select" interval defaultValue={payload} onShow={handleFilterData} />
            </div>
            <div className="mt-3">
              <div className="d-flex justify-content-end">
                {can(Permission.RecordSensorDetailExport) && (
                  <Button
                    variant="primary"
                    className={style.export_btn}
                    icon={FaRegFileExcel}
                    onClick={() => getExcelData()}
                    disabled={!tableData?.length}
                  >
                    {t("buttons.export_to_excel")}
                  </Button>
                )}
              </div>
              <div className="card mt-3">
                <div className="card-body">
                  <div className="overflow-auto">
                    <table className="table table-data table-center">
                      <thead>
                        <tr>
                          <th onClick={() => onSortField("timestamp")} className={style.sort_able}>
                            {t("table.time")} {sortIcon("timestamp")}
                          </th>
                          <th onClick={() => onSortField(isAvgSensor(payload.period, payload.sensor) ? "avg" : "value")} className={style.sort_able}>
                            {t("table.value")} {sortIcon(isAvgSensor(payload.period, payload.sensor) ? "avg" : "value")}
                          </th>
                          <th onClick={() => onSortField("lower-threshold")} className={style.sort_able}>
                            {t("table.lower_threshold")} {sortIcon("lower-threshold")}
                          </th>
                          <th onClick={() => onSortField("upper-threshold")} className={style.sort_able}>
                            {t("table.upper_threshold")} {sortIcon("upper-threshold")}
                          </th>
                          <th
                            onClick={() => onSortField(payload.period == "raw" ? "battery_percentage" : "avg_b_percentage")}
                            className={`${style.sort_able}`}
                          >
                            <div className="d-flex flex-row justify-content-center align-items-center gap-2">
                              <div>
                                {t("table.battery_sensor")}
                                <br />
                                {t("table.status")} (%)
                              </div>
                              {sortIcon(payload.period == "raw" ? "battery_percentage" : "avg_b_percentage")}
                            </div>
                          </th>
                          <th onClick={() => onSortField(payload.period == "raw" ? "delay" : "avg_delay")} className={style.sort_able}>
                            <div className="d-flex flex-row justify-content-center align-items-center gap-2">
                              <div>
                                {t("table.sensor_delay")}
                                <br />
                                {t("table.status")} (ms)
                              </div>
                              {sortIcon(payload.period == "raw" ? "delay" : "avg_delay")}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData?.length ? (
                          tableData?.map((raw, id) => (
                            <tr key={`raw-${id}`}>
                              <td className="text-nowrap">{formatDateWithPeriod(raw.timestamp, payload.period)}</td>
                              <td>{raw?.value}</td>
                              <td>{raw?.min}</td>
                              <td>{raw?.max}</td>
                              <td>{raw?.battery}</td>
                              <td>{raw?.delay}</td>
                            </tr>
                          ))
                        ) : (
                          <tr className="text-center">
                            <td className="text-center py-4" colSpan={8}>
                              {!filtered ? t("fill_the_filter_first") : t("no_data")}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="my-2 d-flex justify-content-end">
                    <ServerPagination current={currentPage} last={data?.totalPages} onPageChange={setCurrentPage} />
                  </div>
                </div>
              </div>
            </div>
          </Fragment>
        ) : (
          <Spinner />
        )}
      </Container>
    </div>
  )
}

export default RecordSensorDataPage
